// MorrowOS billing client helpers — the pricing page + billing-management flows.
//
//   GET  /api/morrow/billing/plans     → public plans (our config, not Stripe)
//   GET  /api/morrow/billing/addons    → public, active add-ons / credit packs
//   POST /api/morrow/billing/checkout  → Stripe Checkout Session URL (plan | add-on)
//   POST /api/morrow/billing/portal    → Stripe billing-portal URL (manage/cancel)
//
// Auth headers are USUALLY attached by installApiFetchAuth's global fetch patch,
// but that layer bails in single-origin dev (paired browser: not cross-origin,
// not desktop), so the Clerk token — which carries the active org — wouldn't ride
// along. For org-scoped endpoints we therefore attach the token explicitly.
import { apiUrl } from "./apiBase";
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";

/** Auth headers for an org-scoped billing request: Clerk token (carries the org) + owner id. */
async function orgAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: "application/json" };
  const token = await getClerkToken().catch(() => null);
  if (token) headers["x-clerk-token"] = token;
  const ownerId = getOwnerId();
  if (ownerId) headers["x-morrow-owner-id"] = ownerId;
  return headers;
}

export interface PlanEntitlement {
  readonly capability: string;
  readonly bool: boolean | null;
  readonly quantity: number | null;
  readonly usageLimit: number | null;
  readonly rateLimit: number | null;
}

export interface BillingPlan {
  readonly slug: string;
  readonly name: string;
  readonly audience: "individual" | "business";
  readonly tier: string;
  readonly rank: number;
  readonly priceCents: number;
  readonly currency: string;
  readonly interval: string;
  readonly seatsIncluded: number;
  readonly entitlements: ReadonlyArray<PlanEntitlement>;
}

/**
 * A one-off credit "fund" pack or a recurring booster the customer can buy on
 * top of their base plan. `interval` is null for one-off packs.
 */
export interface BillingAddon {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly kind: string;
  readonly priceCents: number;
  readonly currency: string;
  readonly interval: string | null;
  readonly entitlements: ReadonlyArray<PlanEntitlement>;
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

/** Fetch the public plan catalog, or null on failure. */
export async function fetchPlans(): Promise<ReadonlyArray<BillingPlan> | null> {
  try {
    const res = await fetch(apiUrl("/api/morrow/billing/plans"), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    const raw = Array.isArray(data.plans) ? data.plans : [];
    return raw.map((entry) => {
      const p = (entry ?? {}) as Record<string, unknown>;
      return {
        slug: str(p.slug),
        name: str(p.name),
        audience: p.audience === "business" ? "business" : "individual",
        tier: str(p.tier, "free"),
        rank: num(p.rank),
        priceCents: num(p.priceCents),
        currency: str(p.currency, "usd"),
        interval: str(p.interval, "month"),
        seatsIncluded: num(p.seatsIncluded),
        entitlements: parseEntitlements(p.entitlements),
      } satisfies BillingPlan;
    });
  } catch {
    return null;
  }
}

function parseEntitlements(raw: unknown): ReadonlyArray<PlanEntitlement> {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((e) => {
    const c = (e ?? {}) as Record<string, unknown>;
    return {
      capability: str(c.capability),
      bool: typeof c.bool === "boolean" ? c.bool : null,
      quantity: typeof c.quantity === "number" ? c.quantity : null,
      usageLimit: typeof c.usageLimit === "number" ? c.usageLimit : null,
      rateLimit: typeof c.rateLimit === "number" ? c.rateLimit : null,
    };
  });
}

/** Fetch the public add-on / credit-pack catalog, or null on failure. */
export async function fetchAddons(): Promise<ReadonlyArray<BillingAddon> | null> {
  try {
    const res = await fetch(apiUrl("/api/morrow/billing/addons"), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    const raw = Array.isArray(data.addons) ? data.addons : [];
    return raw.map((entry) => {
      const a = (entry ?? {}) as Record<string, unknown>;
      return {
        id: str(a.id),
        slug: str(a.slug),
        name: str(a.name),
        kind: str(a.kind, "addon"),
        priceCents: num(a.priceCents),
        currency: str(a.currency, "usd"),
        interval: typeof a.interval === "string" ? a.interval : null,
        entitlements: parseEntitlements(a.entitlements),
      } satisfies BillingAddon;
    });
  } catch {
    return null;
  }
}

/** One model's real token usage for the calling subject, priced at list rates. */
export interface ModelUsage {
  readonly model: string;
  /** Friendly family label from the rate table, or null when unrecognised. */
  readonly family: string | null;
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly events: number;
  /** Estimated USD at full API rate, or null when the model is unpriced. */
  readonly costUsd: number | null;
  readonly priced: boolean;
}

/** One day's rolled-up cost + tokens, for the daily chart. */
export interface DailyUsage {
  readonly date: string;
  readonly costUsd: number;
  readonly tokens: number;
}

/** The customer usage view: per-model breakdown, daily series, and totals. */
export interface UsageSummary {
  readonly days: number;
  readonly estimated: boolean;
  readonly models: ReadonlyArray<ModelUsage>;
  readonly daily: ReadonlyArray<DailyUsage>;
  readonly totals: {
    readonly totalCostUsd: number;
    readonly totalTokens: number;
    readonly inputTokens: number;
    readonly outputTokens: number;
    readonly events: number;
    readonly unpricedModels: number;
  };
}

const EMPTY_USAGE: UsageSummary = {
  days: 30,
  estimated: true,
  models: [],
  daily: [],
  totals: {
    totalCostUsd: 0,
    totalTokens: 0,
    inputTokens: 0,
    outputTokens: 0,
    events: 0,
    unpricedModels: 0,
  },
};

function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * Fetch the calling subject's real AI usage + estimated full-rate cost over a
 * trailing `days` window from `GET /api/morrow/billing/usage`. Returns an empty
 * (all-zero) summary on any failure or when signed out, so the page renders a
 * clean empty state rather than crashing.
 */
export async function fetchUsage(days = 30): Promise<UsageSummary> {
  try {
    const res = await fetch(apiUrl(`/api/morrow/billing/usage?days=${encodeURIComponent(days)}`), {
      headers: await orgAuthHeaders(),
    });
    if (!res.ok) return { ...EMPTY_USAGE, days };
    const data = (await res.json()) as Record<string, unknown>;
    const rawModels = Array.isArray(data.models) ? data.models : [];
    const rawDaily = Array.isArray(data.daily) ? data.daily : [];
    const t = (data.totals ?? {}) as Record<string, unknown>;
    return {
      days: num(data.days) || days,
      estimated: data.estimated !== false,
      models: rawModels
        .map((entry) => {
          const m = (entry ?? {}) as Record<string, unknown>;
          return {
            model: str(m.model),
            family: typeof m.family === "string" ? m.family : null,
            inputTokens: num(m.inputTokens),
            outputTokens: num(m.outputTokens),
            events: num(m.events),
            costUsd: numOrNull(m.costUsd),
            priced: m.priced === true,
          } satisfies ModelUsage;
        })
        .filter((m) => m.model.length > 0 || m.inputTokens > 0 || m.outputTokens > 0),
      daily: rawDaily.map((entry) => {
        const d = (entry ?? {}) as Record<string, unknown>;
        return {
          date: str(d.date),
          costUsd: num(d.costUsd),
          tokens: num(d.tokens),
        } satisfies DailyUsage;
      }),
      totals: {
        totalCostUsd: num(t.totalCostUsd),
        totalTokens: num(t.totalTokens),
        inputTokens: num(t.inputTokens),
        outputTokens: num(t.outputTokens),
        events: num(t.events),
        unpricedModels: num(t.unpricedModels),
      },
    } satisfies UsageSummary;
  } catch {
    return { ...EMPTY_USAGE, days };
  }
}

async function postForUrl(path: string, body: Record<string, unknown>): Promise<string | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    return typeof data.url === "string" ? data.url : null;
  } catch {
    return null;
  }
}

/**
 * Start Stripe Checkout for a plan. Returns the session URL to redirect to, or
 * null if the plan can't be purchased (free/unconfigured) or checkout failed.
 */
export function startCheckout(planSlug: string): Promise<string | null> {
  const returnUrl = typeof window !== "undefined" ? window.location.origin : undefined;
  return postForUrl("/api/morrow/billing/checkout", {
    planSlug,
    ...(returnUrl ? { returnUrl } : {}),
  });
}

/**
 * Start Stripe Checkout for an add-on / credit pack. Returns the session URL to
 * redirect to, or null if the add-on can't be purchased or checkout failed.
 */
export function startAddonCheckout(addonSlug: string): Promise<string | null> {
  const returnUrl = typeof window !== "undefined" ? window.location.origin : undefined;
  return postForUrl("/api/morrow/billing/checkout", {
    addonSlug,
    ...(returnUrl ? { returnUrl } : {}),
  });
}

/** Open the Stripe billing portal. Returns the portal URL, or null if none. */
export function openBillingPortal(): Promise<string | null> {
  const returnUrl = typeof window !== "undefined" ? window.location.origin : undefined;
  return postForUrl("/api/morrow/billing/portal", {
    ...(returnUrl ? { returnUrl } : {}),
  });
}
